import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Trash2, Plus, Edit, CheckCircle, ChevronUp, ChevronDown,
  Newspaper, Megaphone, Calendar, BarChart3, DollarSign,
  Image, Heart, ImageIcon, Users, MessageSquare, Settings,
  Video, Globe, Mail, Menu, X, Building2, LogOut, Shield, FolderOpen, Link as LinkIcon,
  Upload, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { RichTextEditor } from '../components/RichTextEditor';
import { ImagePicker } from '../components/ImagePicker';
import { UploadFolderGrid } from '../components/UploadFolderGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (t) =>
  t.toLowerCase()
    .replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s')
    .replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c')
    .replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

// ── Video file uploader (direct video) ─────────────────────────────────
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB
function VideoFilePicker({ onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow reselecting same file
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Lütfen bir video dosyası seçin');
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video çok büyük (max 100 MB)');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload-video`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Yükleme başarısız');
      }
      const data = await res.json();
      const url = data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
      onUploaded(url);
      toast.success('Video yüklendi');
    } catch (err) {
      toast.error(err.message || 'Yükleme hatası');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        onChange={handleSelect}
        className="hidden"
        data-testid="video-file-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-amber-300 rounded-md bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition-colors text-amber-700 px-3 py-2 text-sm disabled:opacity-60"
        data-testid="video-file-upload"
      >
        {uploading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</>
        ) : (
          <><Upload className="w-4 h-4" /> Bilgisayardan / Telefondan Dosya Seç</>
        )}
      </button>
      <p className="text-[11px] text-amber-600/80 mt-1">MP4, WEBM, MOV · max 100 MB</p>
    </div>
  );
}

// ── Reusable sortable row ──────────────────────────────────────────────
function SortableRow({ index, total, onUp, onDown, onEdit, onDelete, testId, children }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 transition-colors group" data-testid={testId}>
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button onClick={onUp} disabled={index === 0} className="p-0.5 rounded text-gray-400 hover:text-amber-600 disabled:opacity-20 hover:bg-amber-50" data-testid={`${testId}-up`}>
          <ChevronUp className="w-4 h-4" />
        </button>
        <button onClick={onDown} disabled={index === total - 1} className="p-0.5 rounded text-gray-400 hover:text-amber-600 disabled:opacity-20 hover:bg-amber-50" data-testid={`${testId}-down`}>
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-bold flex-shrink-0">{index + 1}</span>
      <div className="flex-1 min-w-0">{children}</div>
      <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {onEdit && (
          <Button onClick={onEdit} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`${testId}-edit`}>
            <Edit className="w-3.5 h-3.5" />
          </Button>
        )}
        <Button onClick={onDelete} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`${testId}-delete`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Form panel (create / edit) ─────────────────────────────────────────
function FormPanel({ title, onCancel, isEdit, children, onSubmit, loading }) {
  return (
    <Card className={`border-2 mb-4 ${isEdit ? 'border-amber-400 shadow-amber-100 shadow-md' : 'border-amber-200'}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-amber-900 text-base">{isEdit ? `Düzenleniyor: ${title}` : `Yeni ${title} Ekle`}</CardTitle>
        {isEdit && <Button onClick={onCancel} variant="ghost" size="sm" className="h-7 text-gray-500"><X className="w-4 h-4 mr-1" />İptal</Button>}
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          {children}
          <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700">
            {isEdit ? <><Edit className="w-4 h-4 mr-2" />Kaydet</> : <><Plus className="w-4 h-4 mr-2" />Ekle</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Empty state ────────────────────────────────────────────────────────
function EmptyState({ text }) {
  return <div className="text-center py-8 text-amber-500 text-sm">{text}</div>;
}

export default function Admin({ user }) {
  const [section, setSection] = useState(user?.role === 'admin' ? 'menu' : 'news');
  const [menuTab, setMenuTab] = useState('koyumuz');
  const [editMode, setEditMode] = useState({ type: null, item: null });
  const formScrollRef = useRef(null);
  // Auto-scroll to form when entering edit mode (so user sees populated data)
  useEffect(() => {
    if (editMode.type && formScrollRef.current) {
      formScrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editMode]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // ── Data state ────────────────────────────────────────────────────────
  const [news, setNews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [polls, setPolls] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [obituaries, setObituaries] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [pendingCondolences, setPendingCondolences] = useState([]);
  const [pendingAlbumComments, setPendingAlbumComments] = useState([]);
  const [allComments, setAllComments] = useState([]);
  const [allAlbumComments, setAllAlbumComments] = useState([]);
  const [allCondolences, setAllCondolences] = useState([]);
  const [editingComment, setEditingComment] = useState(null); // { id, text, type }
  const [commentTab, setCommentTab] = useState('pending');
  const [pages, setPages] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [videos, setVideos] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);

  // ── Form state ────────────────────────────────────────────────────────
  const [newsForm, setNewsForm] = useState({ title: '', content: '', image_url: '' });
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '' });
  const [eventForm, setEventForm] = useState({ title: '', description: '', date: '', location: '' });
  const [pollForm, setPollForm] = useState({ title: '', options: ['', ''] });
  const [sliderForm, setSliderForm] = useState({ image_url: '', title: '', description: '', order: 0 });
  const [obituaryForm, setObituaryForm] = useState({ first_name: '', last_name: '', image_url: '', birth_date: '', death_date: '' });
  const [galleryForm, setGalleryForm] = useState({ image_url: '', title: '', description: '', album_id: '' });
  const [galleryBulkUrls, setGalleryBulkUrls] = useState('');
  const [galleryBulkAlbum, setGalleryBulkAlbum] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryFilterAlbum, setGalleryFilterAlbum] = useState(''); // '', 'none', or album.id
  const [showUploadFolder, setShowUploadFolder] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [albumForm, setAlbumForm] = useState({ name: '', slug: '', description: '', cover_image: '', order: 0 });
  const [navMenus, setNavMenus] = useState([]);
  const [navForm, setNavForm] = useState({ label: '', url: '', parent_id: '', order: 0, is_external: false });
  const [donationForm, setDonationForm] = useState({ bank_name: '', iban: '', account_holder: '' });
  const [pageForm, setPageForm] = useState({ title: '', slug: '', content: '', category: 'koyumuz', order: 0, image_url: '', nav_menu_id: '' });
  const [assocForm, setAssocForm] = useState({ name: '', slug: '', about: '', president_name: '', president_image: '', board_members: [], image_url: '', order: 0 });
  const [videoForm, setVideoForm] = useState({ title: '', url: '', video_type: 'youtube', thumbnail_url: '', description: '', order: 0 });
  const [settingsForm, setSettingsForm] = useState({ about_content: '', address: '', phone: '', email: '', map_url: '', slider_interval: 5 });
  const [boardMemberInput, setBoardMemberInput] = useState({ name: '', role: '' });

  useEffect(() => { fetchAllData(); }, []);

  // ── Fetch all ─────────────────────────────────────────────────────────
  const fetchAllData = async () => {
    try {
      const [uR,nR,anR,evR,poR,slR,obR,gaR,coR,cdR,pgR,asR,viR,stR,cmR,abR,nmR] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/users`, { headers }),
        fetch(`${BACKEND_URL}/api/news`),
        fetch(`${BACKEND_URL}/api/announcements`),
        fetch(`${BACKEND_URL}/api/events`),
        fetch(`${BACKEND_URL}/api/polls`),
        fetch(`${BACKEND_URL}/api/slider`),
        fetch(`${BACKEND_URL}/api/obituaries`),
        fetch(`${BACKEND_URL}/api/gallery`),
        fetch(`${BACKEND_URL}/api/admin/comments/pending`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/condolences/pending`, { headers }),
        fetch(`${BACKEND_URL}/api/pages`),
        fetch(`${BACKEND_URL}/api/associations`),
        fetch(`${BACKEND_URL}/api/videos`),
        fetch(`${BACKEND_URL}/api/site-settings`),
        fetch(`${BACKEND_URL}/api/admin/contact-messages`, { headers }),
        fetch(`${BACKEND_URL}/api/albums`),
        fetch(`${BACKEND_URL}/api/nav-menus`),
      ]);
      if (uR.ok) setUsers(await uR.json());
      if (nR.ok) setNews(await nR.json());
      if (anR.ok) setAnnouncements(await anR.json());
      if (evR.ok) setEvents(await evR.json());
      if (poR.ok) setPolls(await poR.json());
      if (slR.ok) setSliders(await slR.json());
      if (obR.ok) setObituaries(await obR.json());
      if (gaR.ok) setGallery(await gaR.json());
      if (coR.ok) setPendingComments(await coR.json());
      if (cdR.ok) setPendingCondolences(await cdR.json());
      // All comments, condolences, album comments
      const [acR, acdR, paR, aaR] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/comments/all`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/condolences/all`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/album-comments/pending`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/album-comments/all`, { headers }),
      ]);
      if (acR.ok) setAllComments(await acR.json());
      if (acdR.ok) setAllCondolences(await acdR.json());
      if (paR.ok) setPendingAlbumComments(await paR.json());
      if (aaR.ok) setAllAlbumComments(await aaR.json());
      if (pgR.ok) setPages(await pgR.json());
      if (asR.ok) setAssociations(await asR.json());
      if (viR.ok) setVideos(await viR.json());
      if (stR.ok) {
        const s = await stR.json();
        setSettingsForm({ about_content: s.about_content||'', address: s.address||'', phone: s.phone||'', email: s.email||'', map_url: s.map_url||'', slider_interval: s.slider_interval || 5 });
      }
      if (cmR.ok) setContactMessages(await cmR.json());
      if (abR.ok) setAlbums(await abR.json());
      if (nmR.ok) setNavMenus(await nmR.json());
    } catch { toast.error('Veriler yüklenirken hata oluştu'); }
  };

  // ── Generic reorder helper ────────────────────────────────────────────
  const reorder = async (items, idx, dir, endpoint, toPayload) => {
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const a = items[idx], b = items[swapIdx];
    try {
      await Promise.all([
        fetch(`${BACKEND_URL}/api/${endpoint}/${a.id}`, { method:'PUT', headers, body: JSON.stringify(toPayload(a, b.order)) }),
        fetch(`${BACKEND_URL}/api/${endpoint}/${b.id}`, { method:'PUT', headers, body: JSON.stringify(toPayload(b, a.order)) }),
      ]);
      fetchAllData();
    } catch { toast.error('Sıralama güncellenemedi'); }
  };

  // ── Generic CRUD helpers ──────────────────────────────────────────────
  const apiCall = async (method, path, body) => {
    const r = await fetch(`${BACKEND_URL}/api${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
    return r.json().catch(() => ({}));
  };

  const resetEdit = () => setEditMode({ type: null, item: null });

  // ── NEWS ──────────────────────────────────────────────────────────────
  const handleSaveNews = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'news') { await apiCall('PUT', `/news/${editMode.item.id}`, newsForm); toast.success('Haber güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/news', newsForm); toast.success('Haber eklendi'); }
      setNewsForm({ title:'', content:'', image_url:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditNews = (item) => { setNewsForm({ title:item.title, content:item.content, image_url:item.image_url||'' }); setEditMode({ type:'news', item }); };
  const deleteNews = async (id) => { if (!window.confirm('Bu haberi silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/news/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── ANNOUNCEMENTS ─────────────────────────────────────────────────────
  const handleSaveAnn = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'ann') { await apiCall('PUT', `/announcements/${editMode.item.id}`, announcementForm); toast.success('Duyuru güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/announcements', announcementForm); toast.success('Duyuru eklendi'); }
      setAnnouncementForm({ title:'', content:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditAnn = (item) => { setAnnouncementForm({ title:item.title, content:item.content }); setEditMode({ type:'ann', item }); };
  const deleteAnn = async (id) => { if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/announcements/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── EVENTS ────────────────────────────────────────────────────────────
  const handleSaveEvent = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'event') { await apiCall('PUT', `/events/${editMode.item.id}`, eventForm); toast.success('Etkinlik güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/events', eventForm); toast.success('Etkinlik eklendi'); }
      setEventForm({ title:'', description:'', date:'', location:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditEvent = (item) => { setEventForm({ title:item.title, description:item.description, date:item.date, location:item.location }); setEditMode({ type:'event', item }); };
  const deleteEvent = async (id) => { if (!window.confirm('Bu etkinliği silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/events/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── POLLS ─────────────────────────────────────────────────────────────
  const handleSavePoll = async (e) => {
    e.preventDefault(); setLoading(true);
    const opts = pollForm.options.filter(o => o.trim());
    if (opts.length < 2) { toast.error('En az 2 seçenek gerekli'); setLoading(false); return; }
    try {
      const body = { title: pollForm.title, options: opts };
      if (editMode.type === 'poll') { await apiCall('PUT', `/polls/${editMode.item.id}`, body); toast.success('Anket güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/polls', body); toast.success('Anket oluşturuldu'); }
      setPollForm({ title:'', options:['',''] });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditPoll = (item) => { setPollForm({ title:item.title, options:item.options }); setEditMode({ type:'poll', item }); };
  const deletePoll = async (id) => { if (!window.confirm('Bu anketi silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/polls/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── SLIDER ────────────────────────────────────────────────────────────
  const handleSaveSlider = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'slider') { await apiCall('PUT', `/slider/${editMode.item.id}`, sliderForm); toast.success('Slider güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/slider', sliderForm); toast.success('Slider eklendi'); }
      setSliderForm({ image_url:'', title:'', description:'', order:0 });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditSlider = (item) => { setSliderForm({ image_url:item.image_url, title:item.title||'', description:item.description||'', order:item.order }); setEditMode({ type:'slider', item }); };
  const deleteSlider = async (id) => { if (!window.confirm('Bu slider görselini silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/slider/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };
  const reorderSlider = (idx, dir) => reorder(sliders, idx, dir, 'slider',
    (item, newOrder) => ({ image_url:item.image_url, title:item.title||'', description:item.description||'', order:newOrder }));

  // ── OBITUARIES ────────────────────────────────────────────────────────
  const handleSaveObit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'obit') { await apiCall('PUT', `/obituaries/${editMode.item.id}`, obituaryForm); toast.success('Güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/obituaries', obituaryForm); toast.success('Eklendi'); }
      setObituaryForm({ first_name:'', last_name:'', image_url:'', birth_date:'', death_date:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditObit = (item) => { setObituaryForm({ first_name:item.first_name, last_name:item.last_name, image_url:item.image_url||'', birth_date:item.birth_date, death_date:item.death_date }); setEditMode({ type:'obit', item }); };
  const deleteObit = async (id) => { if (!window.confirm('Bu vefat ilanını silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/obituaries/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── GALLERY ───────────────────────────────────────────────────────────
  const handleSaveGallery = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { ...galleryForm, album_id: galleryForm.album_id || null };
    try {
      if (editMode.type === 'gallery') { await apiCall('PUT', `/gallery/${editMode.item.id}`, payload); toast.success('Güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/gallery', payload); toast.success('Eklendi'); }
      setGalleryForm({ image_url:'', title:'', description:'', album_id:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditGallery = (item) => { setGalleryForm({ image_url:item.image_url, title:item.title||'', description:item.description||'', album_id:item.album_id||'' }); setEditMode({ type:'gallery', item }); };
  const deleteGallery = async (id) => { if (!window.confirm('Bu fotoğrafı silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/gallery/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // Gallery: upload files directly from disk (bulk)
  const handleGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    const uploaded = [];
    const failed = [];
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const r = await fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!r.ok) throw new Error('upload failed');
        const d = await r.json();
        const absUrl = d.url.startsWith('http') ? d.url : `${BACKEND_URL}${d.url}`;
        uploaded.push({ image_url: absUrl, title: file.name.replace(/\.[^.]+$/, ''), description: '', album_id: galleryBulkAlbum || null });
      } catch {
        failed.push(file.name);
      }
    }
    try {
      if (uploaded.length > 0) {
        await apiCall('POST', '/gallery/bulk', uploaded);
        toast.success(`${uploaded.length} görsel eklendi`);
      }
      if (failed.length > 0) toast.error(`${failed.length} dosya yüklenemedi: ${failed.join(', ')}`);
      fetchAllData();
    } catch { toast.error('Toplu kayıt başarısız'); }
    finally { setGalleryUploading(false); }
  };

  // Gallery: bulk add multiple URLs (one per line)
  const handleGalleryBulkUrls = async () => {
    const urls = galleryBulkUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error('En az bir URL girin'); return; }
    setGalleryUploading(true);
    try {
      const payload = urls.map(u => ({ image_url: u, title: '', description: '', album_id: galleryBulkAlbum || null }));
      await apiCall('POST', '/gallery/bulk', payload);
      toast.success(`${urls.length} görsel eklendi`);
      setGalleryBulkUrls('');
      fetchAllData();
    } catch { toast.error('Toplu ekleme başarısız'); }
    finally { setGalleryUploading(false); }
  };

  // ── ALBUMS ────────────────────────────────────────────────────────────
  const handleSaveAlbum = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { ...albumForm, cover_image: albumForm.cover_image || null };
    try {
      if (editMode.type === 'album') { await apiCall('PUT', `/albums/${editMode.item.id}`, payload); toast.success('Albüm güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/albums', payload); toast.success('Albüm eklendi'); }
      setAlbumForm({ name:'', slug:'', description:'', cover_image:'', order:0 });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditAlbum = (item) => { setAlbumForm({ name:item.name, slug:item.slug, description:item.description||'', cover_image:item.cover_image||'', order:item.order||0 }); setEditMode({ type:'album', item }); };
  const deleteAlbum = async (id) => {
    const album = albums.find(a => a.id === id);
    const photoCount = album?.photo_count || 0;
    const photoWarning = photoCount > 0
      ? `\n\nDİKKAT: Bu albümdeki ${photoCount} fotoğraf da kalıcı olarak silinecek!`
      : '';
    if (!window.confirm(`"${album?.name || 'Bu albümü'}" silmek istediğinize emin misiniz?${photoWarning}\n\nBu işlem geri alınamaz.`)) return;
    try { await apiCall('DELETE', `/albums/${id}`); toast.success('Albüm ve fotoğrafları silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };

  // ── NAV MENUS ─────────────────────────────────────────────────────────
  const handleSaveNavMenu = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { ...navForm, parent_id: navForm.parent_id || null, url: navForm.url || null };
    try {
      if (editMode.type === 'nav') { await apiCall('PUT', `/nav-menus/${editMode.item.id}`, payload); toast.success('Menü güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/nav-menus', payload); toast.success('Menü eklendi'); }
      setNavForm({ label:'', url:'', parent_id:'', order:0, is_external:false });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditNavMenu = (item) => { setNavForm({ label:item.label||'', url:item.url||'', parent_id:item.parent_id||'', order:item.order||0, is_external:item.is_external||false }); setEditMode({ type:'nav', item }); };
  const deleteNavMenu = async (id) => {
    const menu = navMenus.find(m => m.id === id);
    const subCount = navMenus.filter(m => m.parent_id === id).length;
    const warning = subCount > 0 ? `\n\nBu menüye bağlı ${subCount} alt menü de silinecek.` : '';
    if (!window.confirm(`"${menu?.label || 'Bu menüyü'}" silmek istediğinize emin misiniz?${warning}`)) return;
    try { await apiCall('DELETE', `/nav-menus/${id}`); toast.success('Menü silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };

  // ── COMMENTS ──────────────────────────────────────────────────────────
  const approveComment = async (id) => { try { await apiCall('PUT', `/admin/comments/${id}/approve`); toast.success('Onaylandı'); fetchAllData(); } catch { toast.error('Hata'); } };
  const unapproveComment = async (id) => { try { await apiCall('PUT', `/admin/comments/${id}/unapprove`); toast.success('Onay kaldırıldı'); fetchAllData(); } catch { toast.error('Hata'); } };
  const approveCondolence = async (id) => { try { await apiCall('PUT', `/admin/condolences/${id}/approve`); toast.success('Onaylandı'); fetchAllData(); } catch { toast.error('Hata'); } };
  const unapproveCondolence = async (id) => { try { await apiCall('PUT', `/admin/condolences/${id}/unapprove`); toast.success('Onay kaldırıldı'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── USERS ─────────────────────────────────────────────────────────────
  const deleteUser = async (id) => {
    if (!window.confirm('Üyeyi silmek istediğinizden emin misiniz?')) return;
    try { await apiCall('DELETE', `/admin/users/${id}`); toast.success('Üye silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };

  // ── DONATION ──────────────────────────────────────────────────────────
  const handleSaveDonation = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await apiCall('POST', '/donation-info', donationForm); toast.success('Kaydedildi'); } catch { toast.error('Hata'); } finally { setLoading(false); }
  };

  // ── MENU: PAGES ───────────────────────────────────────────────────────
  const handleSavePage = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { ...pageForm, nav_menu_id: pageForm.nav_menu_id || null };
      if (editMode.type === 'page') { await apiCall('PUT', `/pages/${editMode.item.id}`, payload); toast.success('Sayfa güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/pages', payload); toast.success('Sayfa eklendi'); }
      setPageForm({ title:'', slug:'', content:'', category:'koyumuz', order:0, image_url:'', nav_menu_id:'' });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditPage = (item) => {
    setMenuTab('koyumuz');
    setPageForm({ title:item.title || '', slug:item.slug || '', content:item.content || '', category:item.category || 'koyumuz', order:item.order || 0, image_url:item.image_url || '', nav_menu_id:item.nav_menu_id || '' });
    setEditMode({ type:'page', item });
  };
  const deletePage = async (id) => { if (!window.confirm('Bu sayfayı silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/pages/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };
  const reorderPages = (idx, dir) => reorder(pages, idx, dir, 'pages',
    (item, newOrder) => ({ title:item.title, slug:item.slug, content:item.content, category:item.category, image_url:item.image_url||'', nav_menu_id:item.nav_menu_id || null, order:newOrder }));

  // ── MENU: ASSOCIATIONS ────────────────────────────────────────────────
  const handleSaveAssoc = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'assoc') { await apiCall('PUT', `/associations/${editMode.item.id}`, assocForm); toast.success('Dernek güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/associations', assocForm); toast.success('Dernek eklendi'); }
      setAssocForm({ name:'', slug:'', about:'', president_name:'', president_image:'', board_members:[], image_url:'', order:0 });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditAssoc = (item) => {
    setMenuTab('dernekler');
    setAssocForm({ name:item.name || '', slug:item.slug || '', about:item.about || '', president_name:item.president_name || '', president_image:item.president_image || '', board_members:item.board_members || [], image_url:item.image_url || '', order:item.order || 0 });
    setEditMode({ type:'assoc', item });
  };
  const deleteAssoc = async (id) => { if (!window.confirm('Bu derneği silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/associations/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };
  const reorderAssoc = (idx, dir) => reorder(associations, idx, dir, 'associations',
    (item, newOrder) => ({ name:item.name, slug:item.slug, about:item.about, president_name:item.president_name, president_image:item.president_image||'', board_members:item.board_members||[], image_url:item.image_url||'', order:newOrder }));

  // ── MENU: VIDEOS ──────────────────────────────────────────────────────
  const handleSaveVideo = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editMode.type === 'video') { await apiCall('PUT', `/videos/${editMode.item.id}`, videoForm); toast.success('Video güncellendi'); resetEdit(); }
      else { await apiCall('POST', '/videos', videoForm); toast.success('Video eklendi'); }
      setVideoForm({ title:'', url:'', video_type:'youtube', thumbnail_url:'', description:'', order:0 });
      fetchAllData();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const startEditVideo = (item) => {
    setMenuTab('videos');
    setVideoForm({ title:item.title || '', url:item.url || '', video_type:item.video_type || 'youtube', thumbnail_url:item.thumbnail_url || '', description:item.description || '', order:item.order || 0 });
    setEditMode({ type:'video', item });
  };
  const deleteVideo = async (id) => { if (!window.confirm('Bu videoyu silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/videos/${id}`); toast.success('Silindi'); fetchAllData(); } catch { toast.error('Hata'); } };
  const reorderVideo = (idx, dir) => reorder(videos, idx, dir, 'videos',
    (item, newOrder) => ({ title:item.title, url:item.url, video_type:item.video_type, thumbnail_url:item.thumbnail_url||'', description:item.description||'', order:newOrder }));

  // ── MENU: SITE SETTINGS ───────────────────────────────────────────────
  const handleSaveSettings = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await apiCall('PUT', '/site-settings', settingsForm); toast.success('Ayarlar kaydedildi'); } catch { toast.error('Hata'); } finally { setLoading(false); }
  };

  const saveSiteSettings = async () => {
    setLoading(true);
    try { await apiCall('PUT', '/site-settings', settingsForm); toast.success('Slider ayarları kaydedildi'); } catch { toast.error('Hata'); } finally { setLoading(false); }
  };

  const markMessageRead = async (id) => { try { await apiCall('PUT', `/admin/contact-messages/${id}/read`); fetchAllData(); } catch { toast.error('Hata'); } };
  const deleteContactMessage = async (id) => {
    if (!window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) return;
    try { await apiCall('DELETE', `/admin/contact-messages/${id}`); toast.success('Mesaj silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };

  // Role management
  const updateUserRole = async (userId, newRole) => {
    try {
      await apiCall('PUT', `/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Rol güncellendi: ${newRole}`);
      fetchAllData();
    } catch (err) { toast.error(err.message); }
  };

  const toggleChatBan = async (userId, ban) => {
    const action = ban ? 'sohbette engellemek' : 'sohbet engelini kaldırmak';
    if (!window.confirm(`Bu kullanıcıyı ${action} istediğinize emin misiniz?`)) return;
    try {
      await apiCall('PUT', `/admin/users/${userId}/chat-ban`, { chat_banned: ban });
      toast.success(ban ? 'Kullanıcı sohbette engellendi' : 'Sohbet engeli kaldırıldı');
      fetchAllData();
    } catch (err) { toast.error(err.message); }
  };

  // Comment management
  const deleteComment = async (id) => {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    try { await apiCall('DELETE', `/admin/comments/${id}`); toast.success('Yorum silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };
  const saveEditComment = async () => {
    if (!editingComment) return;
    try {
      let endpoint, body;
      if (editingComment.type === 'condolence') {
        endpoint = `/admin/condolences/${editingComment.id}`;
        body = { message: editingComment.text };
      } else if (editingComment.type === 'album') {
        endpoint = `/admin/album-comments/${editingComment.id}`;
        body = { comment: editingComment.text };
      } else {
        endpoint = `/admin/comments/${editingComment.id}`;
        body = { comment: editingComment.text };
      }
      await apiCall('PUT', endpoint, body);
      toast.success('Güncellendi');
      setEditingComment(null);
      fetchAllData();
    } catch { toast.error('Hata'); }
  };
  const deleteCondolence = async (id) => {
    if (!window.confirm('Bu taziye mesajını silmek istediğinize emin misiniz?')) return;
    try { await apiCall('DELETE', `/admin/condolences/${id}`); toast.success('Taziye mesajı silindi'); fetchAllData(); } catch { toast.error('Hata'); }
  };

  // Album comment management
  const approveAlbumComment = async (id) => { try { await apiCall('PUT', `/admin/album-comments/${id}/approve`); toast.success('Onaylandı'); fetchAllData(); } catch { toast.error('Hata'); } };
  const unapproveAlbumComment = async (id) => { try { await apiCall('PUT', `/admin/album-comments/${id}/unapprove`); toast.success('Onay kaldırıldı'); fetchAllData(); } catch { toast.error('Hata'); } };
  const deleteAlbumComment = async (id) => { if (!window.confirm('Bu albüm yorumunu silmek istediğinize emin misiniz?')) return; try { await apiCall('DELETE', `/admin/album-comments/${id}`); toast.success('Yorum silindi'); fetchAllData(); } catch { toast.error('Hata'); } };

  // ── Sidebar items ─────────────────────────────────────────────────────
  const isAdmin = user?.role === 'admin';

  const navItems = [
    { id:'menu',          label:'Sayfa İçerikleri', icon:Settings,     badge:0,             adminOnly:true },
    { id:'navmenus',      label:'Navbar Menüleri',  icon:LinkIcon,     badge:0,             adminOnly:true },
    { id:'slider',        label:'Slider',          icon:Image,        badge:0,             adminOnly:true },
    { id:'news',          label:'Haberler',        icon:Newspaper,    badge:0,             adminOnly:false },
    { id:'announcements', label:'Duyurular',       icon:Megaphone,    badge:0,             adminOnly:false },
    { id:'events',        label:'Etkinlikler',     icon:Calendar,     badge:0,             adminOnly:false },
    { id:'polls',         label:'Anketler',        icon:BarChart3,    badge:0,             adminOnly:true },
    { id:'obituaries',   label:'Vefat Edenler',   icon:Heart,        badge:0,             adminOnly:true },
    { id:'gallery',       label:'Foto Galeri',     icon:ImageIcon,    badge:0,             adminOnly:false },
    { id:'albums',        label:'Albümler',        icon:FolderOpen,   badge:0,             adminOnly:false },
    { id:'comments',      label:'Yorumlar',        icon:MessageSquare,badge: pendingComments.length + pendingCondolences.length + pendingAlbumComments.length, adminOnly:true },
    { id:'users',         label:'Üyeler',          icon:Users,        badge:0,             adminOnly:true },
    { id:'donation',      label:'Bağış Bilgileri', icon:DollarSign,   badge:0,             adminOnly:true },
  ].filter(item => isAdmin || !item.adminOnly);

  const activeLabel = navItems.find(n => n.id === section)?.label || '';

  // ── RENDER HELPERS ────────────────────────────────────────────────────
  const cancelEdit = (resetFn) => { resetEdit(); resetFn(); };

  // =====================================================================
  // ── SECTION RENDERERS ─────────────────────────────────────────────────
  // =====================================================================

  const renderMenu = () => {
    const menuTabs = [
      { id:'koyumuz',  label:'Köyümüz' },
      { id:'dernekler',label:'Derneklerimiz' },
      { id:'videos',   label:'Videolar' },
      { id:'settings', label:'Site Ayarları' },
      { id:'messages', label:`Mesajlar${contactMessages.filter(m=>!m.is_read).length > 0 ? ` (${contactMessages.filter(m=>!m.is_read).length})` : ''}` },
    ];

    return (
      <div>
        {/* Sub tabs */}
        <div className="flex gap-1 bg-amber-50 p-1 rounded-xl mb-6 flex-wrap">
          {menuTabs.map(t => (
            <button key={t.id} onClick={() => { setMenuTab(t.id); resetEdit(); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${menuTab===t.id ? 'bg-amber-600 text-white shadow' : 'text-amber-700 hover:bg-amber-100'}`}
              data-testid={`menu-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* KÖYÜMÜZ PAGES */}
        {menuTab === 'koyumuz' && (
          <div>
            <FormPanel title="Sayfa İçeriği" isEdit={editMode.type==='page'} onCancel={() => cancelEdit(() => setPageForm({ title:'', slug:'', content:'', category:'koyumuz', order:0, image_url:'', nav_menu_id:'' }))} onSubmit={handleSavePage} loading={loading}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Başlık</Label>
                  <Input value={pageForm.title} onChange={e => setPageForm({...pageForm, title:e.target.value, slug: editMode.type==='page' ? pageForm.slug : slugify(e.target.value)})} required data-testid="page-title-input" />
                </div>
                <div>
                  <Label>Slug (URL kısmı)</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400 whitespace-nowrap">{pageForm.nav_menu_id ? '/sayfa/' : '/koyumuz/'}</span>
                    <Input value={pageForm.slug} onChange={e => setPageForm({...pageForm, slug:e.target.value})} required data-testid="page-slug-input" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Bağlı Navbar Menüsü (Opsiyonel)</Label>
                <select
                  value={pageForm.nav_menu_id}
                  onChange={e => setPageForm({...pageForm, nav_menu_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="page-nav-menu-select"
                >
                  <option value="">— Bağlı değil (sadece /koyumuz/ altında) —</option>
                  {(() => {
                    // Show all leaf nav menus (no children) since parent menus act as dropdowns
                    const parentIds = new Set(navMenus.filter(m => m.parent_id).map(m => m.parent_id));
                    const leafMenus = navMenus.filter(m => !parentIds.has(m.id)).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                    // Build a label that shows the parent chain for context
                    const labelFor = (menu) => {
                      const chain = [menu.label];
                      let curr = menu;
                      while (curr.parent_id) {
                        const parent = navMenus.find(m => m.id === curr.parent_id);
                        if (!parent) break;
                        chain.unshift(parent.label);
                        curr = parent;
                      }
                      return chain.join(' › ');
                    };
                    return leafMenus.map(m => (
                      <option key={m.id} value={m.id}>{labelFor(m)}</option>
                    ));
                  })()}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">Bir navbar menüsü seçerseniz, o menüye tıklandığında bu sayfa açılır. URL otomatik olarak <code>/sayfa/{pageForm.slug || '...'}</code> olarak ayarlanır.</p>
              </div>
              <div>
                <Label>İçerik</Label>
                <RichTextEditor value={pageForm.content} onChange={v => setPageForm({...pageForm, content:v})} placeholder="Sayfa içeriğini yazın..." minHeight={200} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kapak Resmi (Opsiyonel)</Label><ImagePicker value={pageForm.image_url} onChange={v => setPageForm({...pageForm, image_url:v})} testId="page-cover" height={120} /></div>
                <div><Label>Sıra No</Label><Input type="number" value={pageForm.order} onChange={e => setPageForm({...pageForm, order:parseInt(e.target.value)||0})} /></div>
              </div>
            </FormPanel>
            <p className="text-xs text-gray-400 mb-2">Sayfa içerikleri — sırayı ↑↓ ile değiştirin</p>
            <div className="space-y-2">
              {pages.length === 0 && <EmptyState text="Henüz sayfa eklenmemiş. Yukarıdan ekleyin." />}
              {pages.map((p, i) => {
                const linkedMenu = p.nav_menu_id ? navMenus.find(m => m.id === p.nav_menu_id) : null;
                const urlPrefix = p.nav_menu_id ? '/sayfa/' : '/koyumuz/';
                return (
                  <SortableRow key={p.id} index={i} total={pages.length} onUp={() => reorderPages(i,'up')} onDown={() => reorderPages(i,'down')} onEdit={() => startEditPage(p)} onDelete={() => deletePage(p.id)} testId={`page-${p.id}`}>
                    <p className="font-medium text-sm text-gray-800">{p.title}</p>
                    <p className="text-xs text-gray-400">{urlPrefix}{p.slug}</p>
                    {linkedMenu && (
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        <span className="inline-block px-1.5 py-0.5 bg-amber-100 rounded">🔗 Menü: {linkedMenu.label}</span>
                      </p>
                    )}
                  </SortableRow>
                );
              })}
            </div>
          </div>
        )}

        {/* DERNEKLER */}
        {menuTab === 'dernekler' && (
          <div>
            <FormPanel title="Dernek" isEdit={editMode.type==='assoc'} onCancel={() => cancelEdit(() => setAssocForm({ name:'', slug:'', about:'', president_name:'', president_image:'', board_members:[], image_url:'', order:0 }))} onSubmit={handleSaveAssoc} loading={loading}>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dernek Adı</Label><Input value={assocForm.name} onChange={e => setAssocForm({...assocForm, name:e.target.value, slug: editMode.type==='assoc' ? assocForm.slug : slugify(e.target.value)})} required data-testid="assoc-name-input" /></div>
                <div><Label>Slug</Label><Input value={assocForm.slug} onChange={e => setAssocForm({...assocForm, slug:e.target.value})} required data-testid="assoc-slug-input" /></div>
              </div>
              <div><Label>Hakkında</Label><RichTextEditor value={assocForm.about} onChange={v => setAssocForm({...assocForm, about:v})} placeholder="Dernek hakkında..." minHeight={150} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Başkan Adı</Label><Input value={assocForm.president_name} onChange={e => setAssocForm({...assocForm, president_name:e.target.value})} required data-testid="assoc-president-input" /></div>
                <div><Label>Başkan Fotoğrafı (Opsiyonel)</Label><ImagePicker value={assocForm.president_image} onChange={v => setAssocForm({...assocForm, president_image:v})} testId="assoc-president-image" height={120} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dernek Fotoğrafı (Opsiyonel)</Label><ImagePicker value={assocForm.image_url} onChange={v => setAssocForm({...assocForm, image_url:v})} testId="assoc-image" height={120} /></div>
                <div><Label>Sıra No</Label><Input type="number" value={assocForm.order} onChange={e => setAssocForm({...assocForm, order:parseInt(e.target.value)||0})} /></div>
              </div>
              {/* Board members */}
              <div>
                <Label className="mb-1 block">Yönetim Kurulu Üyeleri</Label>
                {assocForm.board_members.map((m, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <Input value={m.name} onChange={e => { const b=[...assocForm.board_members]; b[i]={...b[i],name:e.target.value}; setAssocForm({...assocForm,board_members:b}); }} placeholder="Ad Soyad" className="flex-1" />
                    <Input value={m.role} onChange={e => { const b=[...assocForm.board_members]; b[i]={...b[i],role:e.target.value}; setAssocForm({...assocForm,board_members:b}); }} placeholder="Görev" className="flex-1" />
                    <Button type="button" variant="destructive" size="sm" className="h-9 w-9 p-0" onClick={() => setAssocForm({...assocForm,board_members:assocForm.board_members.filter((_,x)=>x!==i)})}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
                <div className="flex gap-2 mt-1">
                  <Input value={boardMemberInput.name} onChange={e => setBoardMemberInput({...boardMemberInput,name:e.target.value})} placeholder="Ad Soyad" className="flex-1" data-testid="board-name-input" />
                  <Input value={boardMemberInput.role} onChange={e => setBoardMemberInput({...boardMemberInput,role:e.target.value})} placeholder="Görev" className="flex-1" data-testid="board-role-input" />
                  <Button type="button" variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => { if(boardMemberInput.name&&boardMemberInput.role){ setAssocForm({...assocForm,board_members:[...assocForm.board_members,boardMemberInput]}); setBoardMemberInput({name:'',role:''}); }}} data-testid="add-board-member-btn"><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            </FormPanel>
            <p className="text-xs text-gray-400 mb-2">Derneklerimiz ({associations.length}/5) — sırayı ↑↓ ile değiştirin</p>
            <div className="space-y-2">
              {associations.length === 0 && <EmptyState text="Henüz dernek eklenmemiş." />}
              {associations.map((a, i) => (
                <SortableRow key={a.id} index={i} total={associations.length} onUp={() => reorderAssoc(i,'up')} onDown={() => reorderAssoc(i,'down')} onEdit={() => startEditAssoc(a)} onDelete={() => deleteAssoc(a.id)} testId={`assoc-${a.id}`}>
                  <p className="font-medium text-sm text-gray-800">{a.name}</p>
                  <p className="text-xs text-gray-400">Başkan: {a.president_name} · {a.board_members?.length||0} yönetim kurulu üyesi</p>
                </SortableRow>
              ))}
            </div>
          </div>
        )}

        {/* VIDEOS */}
        {menuTab === 'videos' && (
          <div>
            <FormPanel title="Video" isEdit={editMode.type==='video'} onCancel={() => cancelEdit(() => setVideoForm({ title:'', url:'', video_type:'youtube', thumbnail_url:'', description:'', order:0 }))} onSubmit={handleSaveVideo} loading={loading}>
              <div><Label>Video Başlığı</Label><Input value={videoForm.title} onChange={e => setVideoForm({...videoForm, title:e.target.value})} required data-testid="video-title-input" /></div>
              <div>
                <Label>Video URL</Label>
                <Input value={videoForm.url} onChange={e => setVideoForm({...videoForm, url:e.target.value})} placeholder="https://youtube.com/watch?v=... veya direkt video URL" required data-testid="video-url-input" />
                {videoForm.video_type === 'direct' && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">veya dosya yükleyin (link yerine):</p>
                    <VideoFilePicker onUploaded={(url) => setVideoForm({...videoForm, url})} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Video Türü</Label>
                  <select value={videoForm.video_type} onChange={e => setVideoForm({...videoForm, video_type:e.target.value})} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="video-type-select">
                    <option value="youtube">YouTube</option>
                    <option value="direct">Direkt Video</option>
                  </select>
                </div>
                <div><Label>Thumbnail (Opsiyonel)</Label><ImagePicker value={videoForm.thumbnail_url} onChange={v => setVideoForm({...videoForm, thumbnail_url:v})} testId="video-thumb" height={120} /></div>
              </div>
              <div><Label>Açıklama (Opsiyonel)</Label><Textarea value={videoForm.description} onChange={e => setVideoForm({...videoForm, description:e.target.value})} rows={2} /></div>
            </FormPanel>
            <div className="space-y-2">
              {videos.length === 0 && <EmptyState text="Henüz video eklenmemiş." />}
              {videos.map((v, i) => (
                <SortableRow key={v.id} index={i} total={videos.length} onUp={() => reorderVideo(i,'up')} onDown={() => reorderVideo(i,'down')} onEdit={() => startEditVideo(v)} onDelete={() => deleteVideo(v.id)} testId={`video-${v.id}`}>
                  <p className="font-medium text-sm text-gray-800">{v.title}</p>
                  <p className="text-xs text-gray-400">{v.video_type === 'youtube' ? '▶ YouTube' : '▶ Direkt'} · {v.url.substring(0,50)}...</p>
                </SortableRow>
              ))}
            </div>
          </div>
        )}

        {/* SITE SETTINGS */}
        {menuTab === 'settings' && (
          <Card className="border-amber-200 border-2">
            <CardHeader><CardTitle className="text-amber-900 text-base">Site Ayarları — Hakkımızda & İletişim</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSettings} className="space-y-4">
                <div>
                  <Label>Hakkımızda İçeriği</Label>
                  <RichTextEditor value={settingsForm.about_content} onChange={v => setSettingsForm({...settingsForm, about_content:v})} placeholder="Dernek ve köy hakkında bilgi..." minHeight={200} />
                </div>
                <div>
                  <Label>Adres</Label>
                  <Textarea value={settingsForm.address} onChange={e => setSettingsForm({...settingsForm, address:e.target.value})} rows={2} placeholder="Adres bilgisi" data-testid="settings-address-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Telefon</Label><Input value={settingsForm.phone} onChange={e => setSettingsForm({...settingsForm, phone:e.target.value})} placeholder="0555 000 0000" data-testid="settings-phone-input" /></div>
                  <div><Label>E-posta</Label><Input type="email" value={settingsForm.email} onChange={e => setSettingsForm({...settingsForm, email:e.target.value})} placeholder="info@kiziloren.com" data-testid="settings-email-input" /></div>
                </div>
                <div><Label>Google Maps Embed URL</Label><Input value={settingsForm.map_url} onChange={e => setSettingsForm({...settingsForm, map_url:e.target.value})} placeholder="https://maps.google.com/..." data-testid="settings-map-input" /></div>
                <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="save-settings-btn">Ayarları Kaydet</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* CONTACT MESSAGES */}
        {menuTab === 'messages' && (
          <div className="space-y-3">
            {contactMessages.length === 0 && <EmptyState text="Henüz iletişim mesajı yok." />}
            {contactMessages.map(msg => (
              <div key={msg.id} className={`p-4 rounded-xl border ${msg.is_read ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-300'}`} data-testid={`contact-msg-${msg.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{msg.name}</span>
                      {!msg.is_read && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Yeni</span>}
                      <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{msg.email} {msg.phone && `· ${msg.phone}`}</p>
                    <p className="text-sm text-gray-700">{msg.message}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {!msg.is_read && (
                      <Button onClick={() => markMessageRead(msg.id)} size="sm" className="bg-green-600 hover:bg-green-700" data-testid={`mark-read-${msg.id}`}>
                        <CheckCircle className="w-4 h-4 mr-1" />Okundu
                      </Button>
                    )}
                    <Button onClick={() => deleteContactMessage(msg.id)} size="sm" variant="destructive" data-testid={`delete-msg-${msg.id}`}>
                      <Trash2 className="w-4 h-4 mr-1" />Sil
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSlider = () => (
    <div>
      {/* Slider behavior settings */}
      <Card className="border-amber-200 border-2 mb-4 bg-amber-50/30">
        <CardHeader className="pb-3"><CardTitle className="text-amber-900 text-base">Slider Ayarları</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <Label>Otomatik Geçiş Süresi (saniye)</Label>
              <Input
                type="number"
                min="2"
                max="60"
                value={settingsForm.slider_interval}
                onChange={e => setSettingsForm({...settingsForm, slider_interval: parseInt(e.target.value) || 5})}
                data-testid="slider-interval-input"
              />
              <p className="text-xs text-amber-600 mt-1">Slider fotoğrafları kaç saniyede bir değişsin? (2–60)</p>
            </div>
            <Button type="button" onClick={saveSiteSettings} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-slider-interval-btn">
              Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>

      <FormPanel title="Slider Görseli" isEdit={editMode.type==='slider'} onCancel={() => cancelEdit(() => setSliderForm({ image_url:'', title:'', description:'', order:0 }))} onSubmit={handleSaveSlider} loading={loading}>
        <div>
          <Label className="mb-1 block">Görsel</Label>
          <ImagePicker value={sliderForm.image_url} onChange={v => setSliderForm({...sliderForm, image_url:v})} testId="slider-image" height={180} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Başlık (Opsiyonel)</Label><Input value={sliderForm.title} onChange={e => setSliderForm({...sliderForm, title:e.target.value})} data-testid="slider-title-input" /></div>
          <div><Label>Sıra No</Label><Input type="number" value={sliderForm.order} onChange={e => setSliderForm({...sliderForm, order:parseInt(e.target.value)||0})} data-testid="slider-order-input" /></div>
        </div>
        <div><Label>Açıklama (Opsiyonel)</Label><Textarea value={sliderForm.description} onChange={e => setSliderForm({...sliderForm, description:e.target.value})} rows={2} data-testid="slider-description-input" /></div>
      </FormPanel>
      <div className="space-y-2">
        {sliders.length === 0 && <EmptyState text="Slider görseli eklenmemiş." />}
        {sliders.map((s, i) => (
          <SortableRow key={s.id} index={i} total={sliders.length} onUp={() => reorderSlider(i,'up')} onDown={() => reorderSlider(i,'down')} onEdit={() => startEditSlider(s)} onDelete={() => deleteSlider(s.id)} testId={`slider-${s.id}`}>
            <div className="flex items-center gap-3">
              <img src={s.image_url} alt={s.title||'Slider'} className="w-16 h-10 rounded object-cover flex-shrink-0" />
              <div><p className="text-sm font-medium text-gray-800">{s.title || '(Başlıksız)'}</p><p className="text-xs text-gray-400 truncate max-w-xs">{s.image_url}</p></div>
            </div>
          </SortableRow>
        ))}
      </div>
    </div>
  );

  const renderNews = () => (
    <div>
      <FormPanel title="Haber" isEdit={editMode.type==='news'} onCancel={() => cancelEdit(() => setNewsForm({ title:'', content:'', image_url:'' }))} onSubmit={handleSaveNews} loading={loading}>
        <div><Label>Başlık</Label><Input value={newsForm.title} onChange={e => setNewsForm({...newsForm, title:e.target.value})} required data-testid="news-title-input" /></div>
        <div>
          <Label className="mb-1 block">İçerik</Label>
          <RichTextEditor value={newsForm.content} onChange={v => setNewsForm({...newsForm, content:v})} placeholder="Haber içeriğini yazın..." minHeight={180} />
        </div>
        <div>
          <Label className="mb-1 block">Kapak Resmi (Opsiyonel)</Label>
          <ImagePicker value={newsForm.image_url} onChange={v => setNewsForm({...newsForm, image_url:v})} testId="news-image" height={180} />
        </div>
      </FormPanel>
      <div className="space-y-2">
        {news.length === 0 && <EmptyState text="Haber eklenmemiş." />}
        {news.map((item, i) => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`news-${item.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{item.title}</p>
              <p className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString('tr-TR')} · {item.author}</p>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <Button onClick={() => startEditNews(item)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-news-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deleteNews(item.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-news-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnnouncements = () => (
    <div>
      <FormPanel title="Duyuru" isEdit={editMode.type==='ann'} onCancel={() => cancelEdit(() => setAnnouncementForm({ title:'', content:'' }))} onSubmit={handleSaveAnn} loading={loading}>
        <div><Label>Başlık</Label><Input value={announcementForm.title} onChange={e => setAnnouncementForm({...announcementForm, title:e.target.value})} required data-testid="announcement-title-input" /></div>
        <div>
          <Label className="mb-1 block">İçerik</Label>
          <RichTextEditor value={announcementForm.content} onChange={v => setAnnouncementForm({...announcementForm, content:v})} placeholder="Duyuru içeriğini yazın..." minHeight={150} />
        </div>
      </FormPanel>
      <div className="space-y-2">
        {announcements.length === 0 && <EmptyState text="Duyuru eklenmemiş." />}
        {announcements.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`ann-${item.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800 truncate">{item.title}</p><p className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString('tr-TR')}</p></div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <Button onClick={() => startEditAnn(item)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-ann-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deleteAnn(item.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-ann-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderEvents = () => (
    <div>
      <FormPanel title="Etkinlik" isEdit={editMode.type==='event'} onCancel={() => cancelEdit(() => setEventForm({ title:'', description:'', date:'', location:'' }))} onSubmit={handleSaveEvent} loading={loading}>
        <div><Label>Başlık</Label><Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title:e.target.value})} required data-testid="event-title-input" /></div>
        <div>
          <Label className="mb-1 block">Açıklama</Label>
          <RichTextEditor value={eventForm.description} onChange={v => setEventForm({...eventForm, description:v})} placeholder="Etkinlik açıklamasını yazın..." minHeight={150} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tarih</Label><Input type="datetime-local" value={eventForm.date} onChange={e => setEventForm({...eventForm, date:e.target.value})} required data-testid="event-date-input" /></div>
          <div><Label>Konum</Label><Input value={eventForm.location} onChange={e => setEventForm({...eventForm, location:e.target.value})} required data-testid="event-location-input" /></div>
        </div>
      </FormPanel>
      <div className="space-y-2">
        {events.length === 0 && <EmptyState text="Etkinlik eklenmemiş." />}
        {events.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`event-${item.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800">{item.title}</p><p className="text-xs text-gray-400">{item.location} · {new Date(item.date).toLocaleDateString('tr-TR')}</p></div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <Button onClick={() => startEditEvent(item)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-event-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deleteEvent(item.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-event-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPolls = () => (
    <div>
      <FormPanel title="Anket" isEdit={editMode.type==='poll'} onCancel={() => cancelEdit(() => setPollForm({ title:'', options:['',''] }))} onSubmit={handleSavePoll} loading={loading}>
        <div><Label>Anket Sorusu</Label><Input value={pollForm.title} onChange={e => setPollForm({...pollForm, title:e.target.value})} required data-testid="poll-title-input" /></div>
        <div>
          <Label>Seçenekler</Label>
          {pollForm.options.map((o, i) => (
            <div key={i} className="flex gap-2 mb-1">
              <Input value={o} onChange={e => { const opts=[...pollForm.options]; opts[i]=e.target.value; setPollForm({...pollForm, options:opts}); }} placeholder={`Seçenek ${i+1}`} data-testid={`poll-option-${i}`} />
              {i>1 && <Button type="button" variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => setPollForm({...pollForm, options:pollForm.options.filter((_,x)=>x!==i)})}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="mt-1 text-xs" onClick={() => setPollForm({...pollForm, options:[...pollForm.options,'']})}><Plus className="w-3.5 h-3.5 mr-1" />Seçenek Ekle</Button>
        </div>
      </FormPanel>
      <div className="space-y-2">
        {polls.length === 0 && <EmptyState text="Anket eklenmemiş." />}
        {polls.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`poll-${item.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800">{item.title}</p><p className="text-xs text-gray-400">{item.options?.length} seçenek</p></div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <Button onClick={() => startEditPoll(item)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-poll-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deletePoll(item.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-poll-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderObituaries = () => (
    <div>
      <FormPanel title="Vefat Eden" isEdit={editMode.type==='obit'} onCancel={() => cancelEdit(() => setObituaryForm({ first_name:'', last_name:'', image_url:'', birth_date:'', death_date:'' }))} onSubmit={handleSaveObit} loading={loading}>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Ad</Label><Input value={obituaryForm.first_name} onChange={e => setObituaryForm({...obituaryForm, first_name:e.target.value})} required data-testid="obit-first-name-input" /></div>
          <div><Label>Soyad</Label><Input value={obituaryForm.last_name} onChange={e => setObituaryForm({...obituaryForm, last_name:e.target.value})} required data-testid="obit-last-name-input" /></div>
        </div>
        <div>
          <Label className="mb-1 block">Vesikalık Fotoğraf (Opsiyonel)</Label>
          <ImagePicker value={obituaryForm.image_url} onChange={v => setObituaryForm({...obituaryForm, image_url:v})} testId="obit-image" height={140} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Doğum Tarihi</Label><Input type="date" value={obituaryForm.birth_date} onChange={e => setObituaryForm({...obituaryForm, birth_date:e.target.value})} required data-testid="obit-birth-input" /></div>
          <div><Label>Vefat Tarihi</Label><Input type="date" value={obituaryForm.death_date} onChange={e => setObituaryForm({...obituaryForm, death_date:e.target.value})} required data-testid="obit-death-input" /></div>
        </div>
      </FormPanel>
      <div className="space-y-2">
        {obituaries.length === 0 && <EmptyState text="Vefat kaydı eklenmemiş." />}
        {obituaries.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`obit-${item.id}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {item.image_url ? <img src={item.image_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" /> : <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">{item.first_name[0]}{item.last_name[0]}</div>}
              <div><p className="text-sm font-medium text-gray-800">{item.first_name} {item.last_name}</p><p className="text-xs text-gray-400">{new Date(item.birth_date).toLocaleDateString('tr-TR')} – {new Date(item.death_date).toLocaleDateString('tr-TR')}</p></div>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <Button onClick={() => startEditObit(item)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-obit-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deleteObit(item.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-obit-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderGallery = () => (
    <div>
      <FormPanel title="Galeri Görseli" isEdit={editMode.type==='gallery'} onCancel={() => cancelEdit(() => setGalleryForm({ image_url:'', title:'', description:'', album_id:'' }))} onSubmit={handleSaveGallery} loading={loading}>
        <div>
          <Label className="mb-1 block">Görsel</Label>
          <ImagePicker value={galleryForm.image_url} onChange={v => setGalleryForm({...galleryForm, image_url:v})} testId="gallery-image" height={180} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Başlık (Opsiyonel)</Label><Input value={galleryForm.title} onChange={e => setGalleryForm({...galleryForm, title:e.target.value})} /></div>
          <div><Label>Açıklama (Opsiyonel)</Label><Input value={galleryForm.description} onChange={e => setGalleryForm({...galleryForm, description:e.target.value})} /></div>
          <div>
            <Label>Albüm</Label>
            <select value={galleryForm.album_id} onChange={e => setGalleryForm({...galleryForm, album_id:e.target.value})} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="gallery-album-select">
              <option value="">— Albümsüz —</option>
              {albums.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>
      </FormPanel>

      {/* Bulk upload panel */}
      <Card className="border-amber-200 border-2 mb-4 bg-amber-50/30">
        <CardHeader className="pb-3"><CardTitle className="text-amber-900 text-base">Toplu Görsel Ekle</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-1 block">Hedef Albüm</Label>
            <select value={galleryBulkAlbum} onChange={e => setGalleryBulkAlbum(e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="gallery-bulk-album-select">
              <option value="">— Albümsüz —</option>
              {albums.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="mb-1 block">Bilgisayardan Çoklu Dosya Seç</Label>
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={galleryUploading}
              onChange={e => { handleGalleryFilesUpload(e.target.files); e.target.value = ''; }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-amber-600 file:text-white hover:file:bg-amber-700 file:cursor-pointer cursor-pointer disabled:opacity-60"
              data-testid="gallery-bulk-file-input"
            />
            <p className="text-xs text-gray-400 mt-1">Birden fazla dosya seçebilirsiniz (max 10MB/dosya).</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400"><div className="h-px bg-gray-200 flex-1" />veya<div className="h-px bg-gray-200 flex-1" /></div>
          <div>
            <Label className="mb-1 block">Çoklu URL (her satıra bir URL)</Label>
            <Textarea
              value={galleryBulkUrls}
              onChange={e => setGalleryBulkUrls(e.target.value)}
              placeholder={'https://...\nhttps://...'}
              rows={4}
              data-testid="gallery-bulk-urls-input"
            />
            <Button type="button" onClick={handleGalleryBulkUrls} disabled={galleryUploading || !galleryBulkUrls.trim()} className="mt-2 bg-amber-600 hover:bg-amber-700" data-testid="gallery-bulk-urls-btn">
              <Plus className="w-4 h-4 mr-1" />Tümünü Ekle
            </Button>
          </div>
          {galleryUploading && <p className="text-xs text-amber-600">Yükleniyor…</p>}
        </CardContent>
      </Card>

      {/* Upload folder browser */}
      <Card className="border-amber-200 border-2 mb-4">
        <CardHeader className="pb-3">
          <button
            type="button"
            onClick={() => setShowUploadFolder(v => !v)}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-upload-folder"
          >
            <CardTitle className="text-amber-900 text-base flex items-center gap-2">
              <FolderOpen className="w-4 h-4" /> Upload Klasörü
            </CardTitle>
            <span className="text-xs text-amber-600">{showUploadFolder ? 'Gizle' : 'Göster'}</span>
          </button>
        </CardHeader>
        {showUploadFolder && (
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">
              Yüklenmiş tüm görsel ve videolar. Silme, dosya bir içerikte kullanılmıyorsa yapılabilir;
              kullanımdaysa engellenir ve kullanan içerikler listelenir.
            </p>
            <UploadFolderGrid />
          </CardContent>
        )}
      </Card>

      <div className="bg-amber-50/30 border border-amber-200 rounded-xl p-3 mb-4">
        <Label className="mb-1.5 block text-amber-900">Albüm Seçin</Label>
        <select
          value={galleryFilterAlbum}
          onChange={e => setGalleryFilterAlbum(e.target.value)}
          className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-amber-500"
          data-testid="gallery-filter-album-select"
        >
          <option value="">— Bir albüm seçin —</option>
          {albums.map(a => {
            const cnt = gallery.filter(g => g.album_id === a.id).length;
            return <option key={a.id} value={a.id}>{a.name} ({cnt})</option>;
          })}
          <option value="none">Albümsüz ({gallery.filter(g => !g.album_id).length})</option>
        </select>
      </div>

      {(() => {
        if (galleryFilterAlbum === '') {
          return (
            <div className="bg-white border-2 border-dashed border-amber-200 rounded-2xl p-10 text-center">
              <FolderOpen className="w-12 h-12 mx-auto text-amber-300 mb-3" />
              <p className="text-amber-700 text-sm font-medium">Fotoğrafları görüntülemek için yukarıdan bir albüm seçin</p>
              <p className="text-amber-500 text-xs mt-1">veya &quot;Albümsüz&quot; seçeneğini deneyin</p>
            </div>
          );
        }
        const filtered = galleryFilterAlbum === 'none'
          ? gallery.filter(g => !g.album_id)
          : gallery.filter(g => g.album_id === galleryFilterAlbum);
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.length === 0 && <div className="col-span-4"><EmptyState text="Bu albümde henüz fotoğraf yok." /></div>}
            {filtered.map(item => {
              const album = albums.find(a => a.id === item.album_id);
              return (
                <div key={item.id} className="relative rounded-xl overflow-hidden border border-gray-200 group" data-testid={`gallery-${item.id}`}>
                  <img src={item.image_url} alt={item.title||''} className="w-full h-36 object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button onClick={() => startEditGallery(item)} variant="outline" size="sm" className="h-8 w-8 p-0 bg-white/90 border-0" data-testid={`edit-gallery-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                    <Button onClick={() => deleteGallery(item.id)} variant="destructive" size="sm" className="h-8 w-8 p-0" data-testid={`delete-gallery-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  {album && <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-semibold shadow">{album.name}</div>}
                  {!album && <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-gray-500 text-white text-[10px] font-semibold shadow">Albümsüz</div>}
                  {item.title && <div className="p-1.5 bg-white"><p className="text-xs text-gray-700 truncate">{item.title}</p></div>}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  const renderAlbums = () => (
    <div>
      <FormPanel title="Albüm" isEdit={editMode.type==='album'} onCancel={() => cancelEdit(() => setAlbumForm({ name:'', slug:'', description:'', cover_image:'', order:0 }))} onSubmit={handleSaveAlbum} loading={loading}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Albüm Adı</Label>
            <Input value={albumForm.name} onChange={e => setAlbumForm({...albumForm, name:e.target.value, slug: editMode.type==='album' ? albumForm.slug : slugify(e.target.value)})} required data-testid="album-name-input" />
          </div>
          <div>
            <Label>Slug (URL kısmı)</Label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400 whitespace-nowrap">/galeri/</span>
              <Input value={albumForm.slug} onChange={e => setAlbumForm({...albumForm, slug:e.target.value})} required data-testid="album-slug-input" />
            </div>
          </div>
        </div>
        <div><Label>Açıklama (Opsiyonel)</Label><Textarea value={albumForm.description} onChange={e => setAlbumForm({...albumForm, description:e.target.value})} rows={2} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1 block">Kapak Görseli (Opsiyonel)</Label>
            <ImagePicker value={albumForm.cover_image} onChange={v => setAlbumForm({...albumForm, cover_image:v})} testId="album-cover" height={140} />
            <p className="text-[11px] text-gray-400 mt-1">Boş bırakırsanız son eklenen fotoğraf kapak olur.</p>
          </div>
          <div><Label>Sıra No</Label><Input type="number" value={albumForm.order} onChange={e => setAlbumForm({...albumForm, order:parseInt(e.target.value)||0})} /></div>
        </div>
      </FormPanel>

      <p className="text-xs text-gray-400 mb-2">Albümler ({albums.length})</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {albums.length === 0 && <div className="col-span-3"><EmptyState text="Henüz albüm oluşturulmadı." /></div>}
        {albums.map(a => (
          <div key={a.id} className="relative rounded-xl overflow-hidden border border-gray-200 bg-white group" data-testid={`album-${a.id}`}>
            <div className="aspect-video bg-gradient-to-br from-amber-100 to-orange-100">
              {a.cover_image ? (
                <img src={a.cover_image} alt={a.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-10 h-10 text-amber-300" /></div>
              )}
            </div>
            <div className="p-3">
              <h4 className="font-semibold text-sm text-gray-800 truncate">{a.name}</h4>
              <p className="text-xs text-gray-400 truncate">/galeri/{a.slug} · {a.photo_count || 0} fotoğraf</p>
            </div>
            <div className="absolute inset-x-0 top-0 p-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button onClick={() => startEditAlbum(a)} variant="outline" size="sm" className="h-7 w-7 p-0 bg-white/90 border-amber-300" data-testid={`edit-album-${a.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => deleteAlbum(a.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-album-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderNavMenus = () => {
    const topMenus = navMenus.filter(m => !m.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));

    // Build list of all menus excluding self + descendants (prevent cycles)
    const getDescendants = (id, acc = new Set()) => {
      navMenus.filter(m => m.parent_id === id).forEach(c => {
        acc.add(c.id);
        getDescendants(c.id, acc);
      });
      return acc;
    };
    const editingId = editMode.type === 'nav' ? editMode.item?.id : null;
    const excludeIds = new Set();
    if (editingId) {
      excludeIds.add(editingId);
      getDescendants(editingId).forEach(id => excludeIds.add(id));
    }
    // Flatten tree with indent depth
    const flatten = (parentId, depth) => {
      const out = [];
      navMenus
        .filter(m => (m.parent_id || '') === (parentId || '') && !excludeIds.has(m.id))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach(m => {
          out.push({ ...m, _depth: depth });
          out.push(...flatten(m.id, depth + 1));
        });
      return out;
    };
    const parentOptions = flatten('', 0);

    return (
      <div>
        <FormPanel
          title="Navbar Menü Öğesi"
          isEdit={editMode.type === 'nav'}
          onCancel={() => cancelEdit(() => setNavForm({ label:'', url:'', parent_id:'', order:0, is_external:false }))}
          onSubmit={handleSaveNavMenu}
          loading={loading}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Görünen Ad</Label>
              <Input value={navForm.label} onChange={e => setNavForm({...navForm, label:e.target.value})} required data-testid="nav-label-input" />
            </div>
            <div>
              <Label>URL / Yol</Label>
              <Input value={navForm.url} onChange={e => setNavForm({...navForm, url:e.target.value})} placeholder="/haberler veya https://..." data-testid="nav-url-input" />
              <p className="text-[11px] text-gray-400 mt-1">Alt menüleri varsa boş bırakılabilir.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Üst Menü (alt menü yapmak için)</Label>
              <select
                value={navForm.parent_id}
                onChange={e => setNavForm({...navForm, parent_id:e.target.value})}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="nav-parent-select"
              >
                <option value="">— Üst seviye —</option>
                {parentOptions.map(m => (
                  <option key={m.id} value={m.id}>
                    {'\u00A0\u00A0'.repeat(m._depth)}{m._depth > 0 ? '↳ ' : ''}{m.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Herhangi bir menüyü üst menü seçebilirsiniz (sınırsız derinlik).</p>
            </div>
            <div>
              <Label>Sıra No</Label>
              <Input type="number" value={navForm.order} onChange={e => setNavForm({...navForm, order:parseInt(e.target.value)||0})} data-testid="nav-order-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={navForm.is_external} onChange={e => setNavForm({...navForm, is_external:e.target.checked})} data-testid="nav-external-checkbox" />
            <span className="text-sm text-gray-700">Dış bağlantı (yeni sekmede aç)</span>
          </label>
        </FormPanel>

        <p className="text-xs text-gray-400 mb-2">Üst Menüler ({topMenus.length})</p>
        <div className="space-y-2">
          {topMenus.length === 0 && <EmptyState text="Henüz navbar menüsü oluşturulmadı." />}
          {topMenus.map(m => <NavMenuRow key={m.id} menu={m} depth={0} />)}
        </div>
      </div>
    );
  };

  // Recursive nav menu row for arbitrary nesting depth
  const NavMenuRow = ({ menu, depth }) => {
    const subs = navMenus.filter(s => s.parent_id === menu.id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const isTop = depth === 0;
    return (
      <div className={isTop ? 'border border-gray-200 rounded-xl overflow-hidden bg-white' : ''} data-testid={`nav-menu-${menu.id}`}>
        <div className={`flex items-center justify-between gap-3 ${isTop ? 'p-3 bg-amber-50/40' : 'p-2.5 bg-white border-t border-gray-50'}`} style={{ paddingLeft: isTop ? undefined : `${depth * 24 + 12}px` }}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {!isTop && <span className="text-xs text-gray-400 flex-shrink-0">↳</span>}
            {isTop && <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-bold flex-shrink-0">{menu.order}</span>}
            <div className="min-w-0 flex-1">
              <p className={`font-semibold truncate ${isTop ? 'text-sm text-gray-800' : 'text-sm text-gray-700'}`}>{menu.label}</p>
              <p className="text-xs text-gray-400 truncate">{menu.url || '— Dropdown —'}{menu.is_external ? ' · 🔗 dış' : ''}{subs.length > 0 ? ` · ${subs.length} alt menü` : ''}</p>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button onClick={() => startEditNavMenu(menu)} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-nav-${menu.id}`}><Edit className="w-3.5 h-3.5" /></Button>
            <Button onClick={() => deleteNavMenu(menu.id)} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-nav-${menu.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        {subs.map(s => <NavMenuRow key={s.id} menu={s} depth={depth + 1} />)}
      </div>
    );
  };

  const renderComments = () => {
    const tabs = [
      { id: 'pending', label: `Bekleyenler (${pendingComments.length + pendingCondolences.length + pendingAlbumComments.length})` },
      { id: 'all', label: `Tüm Yorumlar (${allComments.length})` },
      { id: 'condolences', label: `Tüm Taziyeler (${allCondolences.length})` },
      { id: 'albums', label: `Tüm Albüm Yorumları (${allAlbumComments.length})` },
    ];

    const CommentRow = ({ item, type }) => {
      const isEditing = editingComment?.id === item.id;
      const text = type === 'condolence' ? item.message : item.comment;
      // Backend uses `approved` field for all 3 types
      const isApproved = !!item.approved;
      const approveFn = () => {
        if (type === 'condolence') return approveCondolence(item.id);
        if (type === 'album') return approveAlbumComment(item.id);
        return approveComment(item.id);
      };
      const unapproveFn = () => {
        if (type === 'condolence') return unapproveCondolence(item.id);
        if (type === 'album') return unapproveAlbumComment(item.id);
        return unapproveComment(item.id);
      };
      const deleteFn = () => {
        if (type === 'condolence') return deleteCondolence(item.id);
        if (type === 'album') return deleteAlbumComment(item.id);
        return deleteComment(item.id);
      };
      const typeLabel = type === 'condolence' ? 'Taziye' : type === 'album' ? 'Albüm' : 'Haber';

      // Resolve which item this comment belongs to + link
      let parentTitle = null;
      let parentLink = null;
      if (type === 'comment' && item.news_id) {
        const n = news.find(x => x.id === item.news_id);
        parentTitle = n?.title || 'Silinmiş haber';
        if (n) parentLink = `/haberler/${n.id}`;
      } else if (type === 'condolence' && item.obituary_id) {
        const o = obituaries.find(x => x.id === item.obituary_id);
        parentTitle = o ? `${o.first_name} ${o.last_name}` : 'Silinmiş vefat ilanı';
        if (o) parentLink = `/vefat-edenler/${o.id}`;
      } else if (type === 'album' && item.album_id) {
        const a = albums.find(x => x.id === item.album_id);
        parentTitle = a?.name || 'Silinmiş albüm';
        if (a) parentLink = `/galeri/${a.slug}`;
      }

      return (
        <div className={`p-3 rounded-xl border mb-2 ${isApproved ? 'bg-white border-gray-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`${type}-${item.id}`}>
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editingComment.text}
                onChange={e => setEditingComment({...editingComment, text: e.target.value})}
                rows={3}
                className="text-sm"
                data-testid="edit-comment-input"
              />
              <div className="flex gap-2">
                <Button onClick={saveEditComment} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="save-comment-btn">Kaydet</Button>
                <Button onClick={() => setEditingComment(null)} variant="outline" size="sm">İptal</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{item.user_nickname}</span>
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">{typeLabel}</span>
                  {!isApproved && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">Bekliyor</span>}
                  <span className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString('tr-TR')}</span>
                </div>
                {parentTitle && (
                  <div className="mb-1 text-[11px] text-gray-500" data-testid={`${type}-parent-${item.id}`}>
                    <span className="text-gray-400">İlgili: </span>
                    {parentLink ? (
                      <a href={parentLink} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:text-orange-600 hover:underline font-medium">
                        {parentTitle}
                      </a>
                    ) : (
                      <span className="italic text-gray-400">{parentTitle}</span>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{text}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {!isApproved ? (
                  <Button onClick={approveFn} size="sm" className="h-7 bg-green-600 hover:bg-green-700 px-2 text-xs" data-testid={`approve-${type}-${item.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />Onayla
                  </Button>
                ) : (
                  <Button onClick={unapproveFn} size="sm" className="h-7 bg-orange-500 hover:bg-orange-600 px-2 text-xs text-white" data-testid={`unapprove-${type}-${item.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />Onay Kaldır
                  </Button>
                )}
                <Button onClick={() => setEditingComment({ id: item.id, text, type })} variant="outline" size="sm" className="h-7 w-7 p-0 border-amber-300" data-testid={`edit-${type}-${item.id}`}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button onClick={deleteFn} variant="destructive" size="sm" className="h-7 w-7 p-0" data-testid={`delete-${type}-${item.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div>
        <div className="flex gap-1 bg-amber-50 p-1 rounded-xl mb-4 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setCommentTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${commentTab===t.id ? 'bg-amber-600 text-white shadow' : 'text-amber-700 hover:bg-amber-100'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {commentTab === 'pending' && (
          <div>
            <p className="text-xs text-gray-400 mb-3">Onay bekleyen yorumlar</p>
            {pendingComments.length === 0 && pendingCondolences.length === 0 && pendingAlbumComments.length === 0 && <EmptyState text="Onay bekleyen içerik yok." />}
            {pendingComments.map(c => <CommentRow key={c.id} item={c} type="comment" />)}
            {pendingCondolences.map(m => <CommentRow key={m.id} item={m} type="condolence" />)}
            {pendingAlbumComments.map(c => <CommentRow key={c.id} item={c} type="album" />)}
          </div>
        )}
        {commentTab === 'all' && (
          <div>
            {allComments.length === 0 && <EmptyState text="Yorum bulunamadı." />}
            {allComments.map(c => <CommentRow key={c.id} item={c} type="comment" />)}
          </div>
        )}
        {commentTab === 'condolences' && (
          <div>
            {allCondolences.length === 0 && <EmptyState text="Taziye mesajı bulunamadı." />}
            {allCondolences.map(m => <CommentRow key={m.id} item={m} type="condolence" />)}
          </div>
        )}
        {commentTab === 'albums' && (
          <div>
            {allAlbumComments.length === 0 && <EmptyState text="Albüm yorumu bulunamadı." />}
            {allAlbumComments.map(c => <CommentRow key={c.id} item={c} type="album" />)}
          </div>
        )}
      </div>
    );
  };

  const renderUsers = () => (
    <div className="space-y-2">
      {users.map(u => (
        <div key={u.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 group" data-testid={`user-${u.id}`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
              {u.nickname?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-800 truncate">{u.nickname}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  u.role === 'admin' ? 'bg-red-100 text-red-700' :
                  u.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {u.role === 'admin' ? 'YÖNETİCİ' : u.role === 'editor' ? 'EDİTÖR' : 'ÜYE'}
                </span>
              </div>
              <p className="text-xs text-gray-400 truncate">{u.email} {u.phone && `· ${u.phone}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {u.id !== user?.id && (
              <>
                <select
                  value={u.role}
                  onChange={e => updateUserRole(u.id, e.target.value)}
                  className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 cursor-pointer hover:border-amber-400 focus:outline-none focus:border-amber-500"
                  data-testid={`role-select-${u.id}`}
                >
                  <option value="member">Üye</option>
                  <option value="editor">Editör</option>
                  <option value="admin">Yönetici</option>
                </select>
                <Button
                  onClick={() => toggleChatBan(u.id, !u.chat_banned)}
                  variant="outline"
                  size="sm"
                  className={`h-7 text-xs ${u.chat_banned ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
                  data-testid={`chat-ban-${u.id}`}
                >
                  {u.chat_banned ? 'Engeli Kaldır' : 'Sohbette Engelle'}
                </Button>
                <Button onClick={() => deleteUser(u.id)} variant="destructive" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-user-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderDonation = () => (
    <Card className="border-amber-200 border-2 max-w-lg">
      <CardHeader><CardTitle className="text-amber-900 text-base">Bağış Bilgileri</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={handleSaveDonation} className="space-y-3">
          <div><Label>Banka Adı</Label><Input value={donationForm.bank_name} onChange={e => setDonationForm({...donationForm, bank_name:e.target.value})} required data-testid="donation-bank-input" /></div>
          <div><Label>IBAN</Label><Input value={donationForm.iban} onChange={e => setDonationForm({...donationForm, iban:e.target.value})} placeholder="TR00 0000 0000 0000 0000 0000 00" required data-testid="donation-iban-input" /></div>
          <div><Label>Hesap Sahibi</Label><Input value={donationForm.account_holder} onChange={e => setDonationForm({...donationForm, account_holder:e.target.value})} required data-testid="donation-holder-input" /></div>
          <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="save-donation-btn">Kaydet</Button>
        </form>
      </CardContent>
    </Card>
  );

  const sectionMap = {
    menu: renderMenu, slider: renderSlider, news: renderNews,
    announcements: renderAnnouncements, events: renderEvents, polls: renderPolls,
    obituaries: renderObituaries, gallery: renderGallery, albums: renderAlbums, comments: renderComments,
    users: renderUsers, donation: renderDonation, navmenus: renderNavMenus,
  };

  // ── LAYOUT ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full z-40 bg-amber-900 text-white flex flex-col transition-transform duration-300 w-56 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:flex`}>
        <div className="p-4 border-b border-amber-700/50">
          <div className="flex items-center gap-2">
            <img src="/logo-128.png" alt="Kızılören Kasabası logosu" className="w-8 h-8 rounded-full object-cover" />
            <div>
              <p className="font-semibold text-sm leading-tight">Yönetim Paneli</p>
              <p className="text-amber-300 text-xs">{user?.nickname}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setSection(item.id); resetEdit(); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors relative ${section===item.id ? 'bg-amber-700 text-white font-medium' : 'text-amber-200 hover:bg-amber-800 hover:text-white'}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{item.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-amber-700/50">
          <a href="/" className="flex items-center gap-2 text-amber-300 hover:text-white text-xs transition-colors">
            <LogOut className="w-3.5 h-3.5" />
            <span>Siteye Dön</span>
          </a>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 lg:hidden sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-gray-800">{activeLabel}</h1>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Page title (desktop) */}
            <div className="hidden lg:flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{activeLabel}</h2>
            </div>
            {/* Section content */}
            <div ref={formScrollRef} />
            {sectionMap[section]?.()}
          </div>
        </main>
      </div>
    </div>
  );
}
